import ReactGA from 'react-ga4';
import { communicationUtilities } from '@incentivegames/ig-frontend-common/lib/utilities/communicationUtilities';
import { EventStatus } from '@incentivegames/ig-types/lib/enums/event/eventStatus';
import { IEvent, IEventParticipant, IGameEntry, ITeamMember } from '@incentivegames/ig-types/lib/interfaces/apiContracts';
import routes from 'constants/routes';
import { IEventsSummary, IShareEntry, IShareTeamMember } from 'interfaces/gameInterfaces';
import { clearState } from 'store/actions/dreamteamActions';
import { ReactGACategories } from 'constants/appEnums';
import { teamNameMap, teamCodeMap } from 'theme/teams';

export const dreamteamUtilities = {
  /**
   * @param entry IGameEntry to look at
   * @return true if all the teamMembers have been revealed or missed
   */
  isEntryFinished: (entry: IGameEntry): boolean => {
    const timeNow = new Date().getTime();
    let finished = true;
    entry.team.forEach((teamMember: ITeamMember) => {
      if (!teamMember.revealed && teamMember.expiryTime > timeNow) {
        finished = false;
      }
    });
    return finished;
  },

  /**
   * @param isAuthenticated: Function to know if the user is authenticated, usually injected from the userContext
   * @param history: OPTIONAL. If included, the user will be redirected to home, if not, a login postMessage will be sent
   * @return true if the user will be redirected and false if they were already authenticated
   */
  redirectIfNotAuthenticated: (isAuthenticated: () => boolean, dispatch?: any, history?: any): boolean => {
    if (!isAuthenticated()) {
      dispatch(clearState());
      if (history) {
        history.push({
          pathname: routes.HOME.route,
          search: window.location.search
        });
        return true;
      } else {
        communicationUtilities.sendPostMessage.login();
        return true;
      }
    }
    return false;
  },

  getEventSummary: (events: IEvent[]): IEventsSummary => {
    const activeEvents = events.filter((event: IEvent) => event.eventActive);

    if (!activeEvents.length) return {};
    if (activeEvents.length === 1) {
      return {
        firstStartTime: activeEvents[0].eventStartTime,
        lastStartTime: activeEvents[0].eventStartTime,
        atLeastOneLiveGame: activeEvents[0].eventStartTime < new Date().getTime() && activeEvents[0].eventPeriod !== EventStatus.fullTime
      };
    }

    return {
      firstStartTime: activeEvents.reduce((prev, current) => (current.eventStartTime < prev.eventStartTime ? current : prev)).eventStartTime,
      lastStartTime: activeEvents.reduce((prev, current) => (current.eventStartTime > prev.eventStartTime ? current : prev)).eventStartTime,
      atLeastOneLiveGame: activeEvents.some((event: IEvent) => event.eventStartTime < new Date().getTime() && event.eventPeriod !== EventStatus.fullTime),
      allEventsFullTime: !activeEvents.some((event: IEvent) => event.eventPeriod !== EventStatus.fullTime)
    };
  },

  /**
   * Records an event in Google Analytics
   * @param category Type of category. Use the enum
   * @param action Type of action. i.e. failure, create...
   * @param label OPTIONAL
   * @param value OPTIONAL. Numeric value
   */
  recordEventGA: (category: ReactGACategories, action: string, label?: string, value?: number) => {
    ReactGA.event({
      category,
      action,
      label,
      value
    });
  },

  formatCurrencyString: (currencySymbol: string, currencyCode: string, amount: number) => {
    const envLanguage = process.env.REACT_APP_LANGUAGE;
    const isBgLang = envLanguage === 'bg';
    const isFloat = Number(amount) === amount && amount % 1 !== 0;
    let formattedCurrencyString = isBgLang ? `${amount} ${currencySymbol}.` : `${currencySymbol}${amount}`;

    if (isFloat) {
      formattedCurrencyString = isBgLang ? `${amount.toFixed(2)} ${currencySymbol}.` : `${currencySymbol}${amount.toFixed(2)}`;
    }

    return formattedCurrencyString;
  },

  getTeamName: (teamName?: string): string | undefined => {
    return teamName ? teamNameMap.get(teamName) : undefined;
  },

  /**
   * Gets the team code that should be displayed based on the client preferences.
   * If the code has an override in the `theme/team` module, the overridden value will be used.
   * Otherwise, the input `code` will be returned.
   * @param code The team code used by IG internally
   * @returns The overridden team code, if found. Otherwise, the input `code`.
   */
  getTeamCode: (code: string) => teamCodeMap.get(code) ?? code,

  /**
   * Parses an IGameEntry into a IShareEntry
   * @param gameName
   * @param entry
   * @returns {IShareEntry}
   */
  getShareEntry: (gameName: string | undefined, entry: IGameEntry) => {
    const shareEntry: IShareEntry = { gameName, team: [] };
    entry.team.forEach((teamMember: ITeamMember) => {
      const t: IShareTeamMember = {
        selnNumber: teamMember.selnNumber,
        revealed: teamMember.revealed
      };
      if (teamMember.displayName) t.displayName = teamMember.displayName;
      if (teamMember.teamCode) {
        t.teamCode = teamMember.teamCode;
        t.teamName = teamMember.eventParticipants?.find((ep: IEventParticipant) => ep.participantCode === teamMember.teamCode)?.participantName;
      }
      shareEntry.team.push(t);
    });
    return shareEntry;
  }
};
